// Ported from deexoptions-mobile-app/app/survey.tsx.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { IoArrowBack, IoChatbubbleEllipsesOutline } from "react-icons/io5";

import SurveyQuestionView, {
  type AnswerValue,
} from "../../components/survey/SurveyQuestion";
import {
  OTHER_OPTION_ID,
  surveyQueryRoot,
  useActiveSurvey,
  useRecordSurveyView,
  useSaveSurveyResponse,
  useStartSurveyResponse,
  type SurveyAnswerPayload,
  type SurveyPlacement,
  type SurveyQuestion,
} from "../../hooks/queries/useSurvey";
import { errorMessage } from "../../utils/errorMessage";

type Screen = "intro" | "question" | "complete";

/** Answers live in one map keyed by question key, mirroring how the API merges them. */
type AnswerState = Record<
  string,
  { value: AnswerValue; otherText: string; usedSuggestions: string[] }
>;

const blankAnswer = () => ({
  value: null as AnswerValue,
  otherText: "",
  usedSuggestions: [] as string[],
});

/** Whether a required question has been satisfied. */
const isAnswered = (q: SurveyQuestion, a: AnswerState[string] | undefined) => {
  if (!a) return false;
  const { value, otherText } = a;
  if (q.type === "MULTI_CHOICE") {
    const list = Array.isArray(value) ? value : [];
    if (list.length === 0) return false;
    // "Other" without any text is not an answer.
    if (list.includes(OTHER_OPTION_ID) && !otherText.trim()) return false;
    return true;
  }
  if (q.type === "SINGLE_CHOICE") {
    if (typeof value !== "string" || !value) return false;
    if (value === OTHER_OPTION_ID && !otherText.trim()) return false;
    return true;
  }
  if (q.type === "SHORT_TEXT" || q.type === "LONG_TEXT") {
    return typeof value === "string" && value.trim().length > 0;
  }
  if (q.type === "NPS" || q.type === "RATING") return typeof value === "number";
  return true;
};

/** Shapes one answer for the API. Returns null when the question was skipped. */
const toPayload = (
  q: SurveyQuestion,
  a: AnswerState[string] | undefined
): SurveyAnswerPayload | null => {
  if (!a || !isAnswered(q, a)) return null;
  const base: SurveyAnswerPayload = {
    questionKey: q.key,
    value: a.value as string | string[] | number,
  };
  const usesOther =
    (typeof a.value === "string" && a.value === OTHER_OPTION_ID) ||
    (Array.isArray(a.value) && a.value.includes(OTHER_OPTION_ID));
  if (usesOther && a.otherText.trim()) base.otherText = a.otherText.trim();
  if (a.usedSuggestions.length) base.usedSuggestions = a.usedSuggestions;
  return base;
};

const shell = "w-full bg-[#E7EBEE] font-manrope dark:bg-[#13181B]";

/**
 * The column is a fixed-height flex container, not `min-h-screen`: the action button is
 * pinned to the bottom of the viewport and only the content between header and button
 * scrolls, so "Begin survey" / "Next" is never below the fold.
 *
 * `h-screen` is 100vh, which on mobile browsers measures the viewport *without* the
 * retractable URL bar — taller than what is actually visible, which is exactly what pushed
 * the button off-screen. `100dvh` is the visible height; it goes inline so that a browser
 * without dvh support simply drops the declaration and falls back to the 100vh class.
 */
const column = "mx-auto flex h-screen w-full max-w-[600px] flex-col px-4 pb-6 pt-6";
const columnStyle = { height: "100dvh" };
/** `min-h-0` is what actually lets a flex child shrink and scroll instead of overflowing. */
const scrollArea = "min-h-0 flex-1 overflow-y-auto";

const Survey = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const placement = (params.get("placement") ??
    "DASHBOARD_CTA") as SurveyPlacement;

  const queryClient = useQueryClient();
  const { data: survey, isPending } = useActiveSurvey(placement);
  const startMutation = useStartSurveyResponse();
  const saveMutation = useSaveSurveyResponse();
  const recordView = useRecordSurveyView();

  const [screen, setScreen] = useState<Screen>("intro");
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<AnswerState>({});
  const [earnedPoints, setEarnedPoints] = useState(0);

  // The attempt is identified by these two; the token is reissued on resume, so it is held
  // in a ref rather than state — it never needs to trigger a render.
  const attempt = useRef<{ responseId: string; resumeToken: string } | null>(
    null
  );

  // Entry points other than the CTA card — a slider banner, a deep link — would otherwise
  // never be counted. The endpoint is unique-per-user, so overlapping with the card's own
  // call cannot double-count.
  const viewed = useRef<string | null>(null);
  useEffect(() => {
    if (!survey?._id || viewed.current === survey._id) return;
    viewed.current = survey._id;
    recordView.mutate(survey._id);
    // recordView is a stable mutation object; depending on it would re-fire the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey?._id]);

  const questions = useMemo(
    () => [...(survey?.questions ?? [])].sort((a, b) => a.order - b.order),
    [survey]
  );
  const question = questions[index];
  const current = answers[question?.key ?? ""] ?? blankAnswer();
  const total = questions.length;
  const busy = startMutation.isPending || saveMutation.isPending;

  const patchAnswer = useCallback(
    (key: string, patch: Partial<AnswerState[string]>) =>
      setAnswers((prev) => ({
        ...prev,
        [key]: { ...(prev[key] ?? blankAnswer()), ...patch },
      })),
    []
  );

  const fail = (e: any, fallback: string) =>
    toast.error(errorMessage(e?.message || e?.data?.message) || fallback);

  /** Leaves the survey. A direct visit has no history to go back to, so fall to dashboard. */
  const leave = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/dashboard");
  };

  /** Intro → first question. Starts the attempt so partials are recorded from the off. */
  const begin = async () => {
    if (!survey) return;
    try {
      const res = await startMutation.mutateAsync({
        surveyId: survey._id,
        placement,
      });
      const data = res.data?.data;
      attempt.current = {
        responseId: data.responseId,
        resumeToken: data.resumeToken ?? "",
      };
      setScreen("question");
    } catch (e) {
      fail(e, "Could not start the survey.");
    }
  };

  /**
   * Saves the current answer and advances. Saving per question is what makes an abandoned
   * attempt show up as PARTIAL in the admin rather than vanishing.
   */
  const next = async () => {
    if (!survey || !question || !attempt.current) return;

    if (question.required && !isAnswered(question, answers[question.key])) {
      toast.error("This question is required.");
      return;
    }

    const payload = toPayload(question, answers[question.key]);
    const last = index === total - 1;

    try {
      const res = await saveMutation.mutateAsync({
        surveyId: survey._id,
        responseId: attempt.current.responseId,
        resumeToken: attempt.current.resumeToken,
        answers: payload ? [payload] : [],
        submit: last,
      });
      if (last) {
        setEarnedPoints(res.data?.data?.rewardPoints ?? 0);
        setScreen("complete");
        // The banner and the Settings / transaction-success cards read from this cache;
        // without this they keep offering a survey the user has just finished.
        queryClient.invalidateQueries({ queryKey: surveyQueryRoot });
      } else {
        setIndex((i) => i + 1);
      }
    } catch (e) {
      fail(
        e,
        last ? "Could not submit your answers." : "Could not save your answer."
      );
    }
  };

  const back = () => {
    if (screen === "question" && index > 0) {
      setIndex((i) => i - 1);
      return;
    }
    leave();
  };

  /* ------------------------------- loading -------------------------------- */
  if (isPending) {
    return (
      <div className={shell}>
        <div className="flex h-screen items-center justify-center" style={columnStyle}>
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-secondary_button border-t-transparent" />
        </div>
      </div>
    );
  }

  /* ------------------------------- complete -------------------------------
     Rendered before the availability guard on purpose: submitting invalidates the
     active-survey cache, which refetches null (the backend stops offering a finished
     survey), and the thank-you screen must outlive that. */
  if (screen === "complete") {
    return (
      <div className={shell}>
        <div className={column} style={columnStyle}>
          <div className={`${scrollArea} flex flex-col items-center justify-center`}>
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-[#D4EBFD] text-[34px] dark:bg-secondary_button/20">
              🎉
            </div>
            <h1 className="mt-6 text-center font-sora text-[22px] font-bold text-gray-900 dark:text-gray-100">
              Thank you!
            </h1>
            <p className="mt-2 text-center text-[15px] leading-6 text-gray-500 dark:text-gray-400">
              Your feedback helps us build a better DeeX.
            </p>
            {earnedPoints > 0 && (
              <span className="mt-5 rounded-full bg-[#D4EBFD] px-5 py-2.5 text-[14px] font-semibold text-secondary_button dark:bg-secondary_button/20">
                +{earnedPoints} points added
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={leave}
            className="mt-6 w-full shrink-0 rounded-lg bg-secondary_button py-4 text-[15px] font-semibold text-white"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  if (!survey || total === 0) {
    return (
      <div className={shell}>
        <div className={column} style={columnStyle}>
          <div className={`${scrollArea} flex flex-col items-center justify-center`}>
            <p className="text-center text-[15px] text-gray-500 dark:text-gray-400">
              This survey is no longer available.
            </p>
            <button
              type="button"
              onClick={leave}
              className="mt-6 rounded-lg bg-secondary_button px-8 py-3 text-[15px] font-semibold text-white"
            >
              Go back
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* -------------------------------- intro --------------------------------- */
  if (screen === "intro") {
    return (
      <div className={shell}>
        <div className={column} style={columnStyle}>
          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              aria-label="Go back"
              onClick={leave}
              className="text-[22px] text-gray-700 dark:text-gray-200"
            >
              <IoArrowBack />
            </button>
            <h4 className="font-sora text-[16px] font-semibold text-gray-900 dark:text-gray-200">
              Quick survey
            </h4>
          </div>

          {/* `justify-center` keeps the illustration block optically centred on a tall
              screen; it scrolls instead of overflowing on a short one. */}
          <div className={`${scrollArea} flex flex-col items-center justify-center py-6`}>
            {/* Concentric rings: the middle one is the page background, so it reads as a
                gap between the halo and the blue disc in both themes. */}
            <div className="flex h-[116px] w-[116px] shrink-0 items-center justify-center rounded-full bg-[#D4EBFD] dark:bg-secondary_button/20">
              <div className="flex h-[92px] w-[92px] items-center justify-center rounded-full bg-[#E7EBEE] dark:bg-[#13181B]">
                <div className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-secondary_button text-[30px] text-white">
                  <IoChatbubbleEllipsesOutline />
                </div>
              </div>
            </div>

            <h1 className="mt-8 text-center font-sora text-[24px] font-bold leading-8 text-gray-900 dark:text-gray-100">
              Help us make DeeX better
            </h1>

            {!!survey.description && (
              <p className="mt-4 text-center text-[16px] leading-7 text-gray-500 dark:text-gray-400">
                {survey.description}
              </p>
            )}

            <div className="mt-6 flex items-center text-[14px] text-gray-500 dark:text-gray-400">
              <span>
                {total} {total === 1 ? "question" : "questions"}
              </span>
              {survey.isAnonymous && (
                <>
                  <span className="mx-2 h-1 w-1 rounded-full bg-gray-400" />
                  <span>Anonymous</span>
                </>
              )}
            </div>

            {!!survey.rewardPoints && (
              <span className="mt-5 rounded-full bg-[#D4EBFD] px-5 py-2.5 text-[13px] font-semibold text-secondary_button dark:bg-secondary_button/20">
                Earn {survey.rewardPoints} points
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={begin}
            disabled={busy}
            className="w-full shrink-0 rounded-lg bg-secondary_button py-4 text-[15px] font-semibold text-white disabled:opacity-60"
          >
            {busy ? "Starting..." : "Begin survey"}
          </button>
        </div>
      </div>
    );
  }

  /* ------------------------------- question ------------------------------- */
  const progress = ((index + 1) / total) * 100;
  const canSkip = !question.required;

  return (
    <div className={shell}>
      <div className={column} style={columnStyle}>
        <div className="flex shrink-0 items-center gap-3">
          <button
            type="button"
            aria-label="Go back"
            onClick={back}
            className="text-[22px] text-gray-700 dark:text-gray-200"
          >
            <IoArrowBack />
          </button>
          <h4 className="font-sora text-[16px] font-semibold text-gray-900 dark:text-gray-200">
            Question {index + 1} of {total}
          </h4>
        </div>

        <div className="mt-3 h-1.5 w-full shrink-0 rounded-full bg-gray-200 dark:bg-gray-700">
          <div
            className="h-1.5 rounded-full bg-secondary_button transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className={`${scrollArea} pb-6 pt-6`}>
          <h2 className="font-sora text-[19px] font-bold leading-7 text-gray-900 dark:text-gray-100">
            {question.prompt}
          </h2>
          {!!question.helpText && (
            <p className="mt-1.5 text-[14px] leading-5 text-gray-500 dark:text-gray-400">
              {question.helpText}
            </p>
          )}
          {!question.required && (
            <p className="mt-1.5 text-[12px] text-gray-400">Optional</p>
          )}

          <div className="mt-5">
            <SurveyQuestionView
              question={question}
              value={current.value}
              onChange={(value) => patchAnswer(question.key, { value })}
              otherText={current.otherText}
              onOtherTextChange={(otherText) =>
                patchAnswer(question.key, { otherText })
              }
              usedSuggestions={current.usedSuggestions}
              onUsedSuggestionsChange={(usedSuggestions) =>
                patchAnswer(question.key, { usedSuggestions })
              }
            />
          </div>
        </div>

        <div className="shrink-0">
          <button
            type="button"
            onClick={next}
            disabled={busy}
            className="w-full rounded-lg bg-secondary_button py-4 text-[15px] font-semibold text-white disabled:opacity-60"
          >
            {index === total - 1 ? "Submit" : "Next"}
          </button>
          {canSkip && (
            // Skip runs the same save path with an empty answer array, so the attempt
            // still records progress.
            <button
              type="button"
              onClick={next}
              disabled={busy}
              className="mt-1 w-full py-3 text-[14px] font-semibold text-gray-500 dark:text-gray-400"
            >
              Skip
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Survey;
